import React, { useRef, useState } from "react";

import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import ExportIcon from "@mui/icons-material/FileDownload";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import { Popper, Stack, Tooltip } from "@mui/material";
import { Hierarchy } from "assets/material-custom-icons";
import {
  // VisibilityLock,
  WorkspaceCreateTemplateIcon,
  WorkspaceImportTemplateIcon,
  WorkspaceTagPdfIcon
} from "assets/material-custom-icons";
import classnames from "classnames";

import { ContextMenuTheme, MoveToFolder } from "..";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import { ReactComponent as ChevronIcon } from "../../../assets/icons/chevron.svg";
import { ReactComponent as CrossInCircleIcon } from "../../../assets/icons/cross-in-circle.svg";
import { ReactComponent as DataIcon } from "../../../assets/icons/deal-data.svg";
import { ReactComponent as PencilIcon } from "../../../assets/icons/pencil.svg";
import { ReactComponent as TickHalfCircleIcon } from "../../../assets/icons/tick-half-circle.svg";
import { ReactComponent as UploadSharp } from "../../../assets/icons/upload.svg";
import { ReactComponent as VisibilityLock } from "../../../assets/icons/visibility-lock.svg";
import { CustomComponent } from "../../../store/types";

import "./ContextMenu.styles.scss";

export interface IOptionProps {
  onClick: () => Promise<void>;
  title?: string;
  theme: ContextMenuTheme;
  hasLineAfter?: boolean;
  className?: string;
}

const getClassName = (theme: ContextMenuTheme) => classnames("spacer-wrapper", "spacer-wrapper--" + theme);

const OnRenameOption: React.FC<IOptionProps> = ({ onClick, title = "Rename", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <TitleOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnDeleteOption: React.FC<IOptionProps> = ({ onClick, title = "Delete", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <DeleteOutlineIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnDuplicateOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Duplicate",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <ContentCopyOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnDownloadOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Download",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <ExportIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

// const OnMoveToOption: React.FC<IOptionProps> = ({
//   onClick,
//   title = "Move to...",
//   theme,
//   hasLineAfter,
// }): JSX.Element => (
//   <div
//     onClick={onClick}
//     className={classnames("option", "option--" + theme, {
//       "option-underline": hasLineAfter,
//     })}>
//     <div className={getClassName(theme)}>
//       <MoveToIcon className="icon" />
//       <span className="title">{title}</span>
//     </div>
//   </div>
// );

const OnShareOption: React.FC<IOptionProps> = ({ onClick, title = "Share", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <PersonAddOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnEditOption: React.FC<IOptionProps> = ({ onClick, title = "Edit", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <PencilIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnAddFormOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Add a New Form",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <ListAltIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnLinkOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Link to Comment",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <PersonAddOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnUploadOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Upload File",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    data-testid="context-menu-on-upload-file"
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <input
        data-testid="file-uploader"
        type="file"
        multiple
        className="context-menu__input--hide"
        onChange={onClick}
      />
      <div className="icon-wrapper">
        <FileUploadOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnSaveDealAsTemplateOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Create Template from Deal",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <WorkspaceCreateTemplateIcon className="icon" sx={{ fontSize: "20px" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnCreateFolderOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Add a New Folder",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    data-testid="context-menu-on-create-folder"
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <CreateNewFolderOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMarkAsReadOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Mark as Read",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <TickHalfCircleIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMarkAsUnreadOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Mark as Unread",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <CreateNewFolderOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnRemoveNotificationOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Remove Notification",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <CrossInCircleIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnConvertToPeriodic: React.FC<IOptionProps> = ({
  onClick,
  title = "Recurring Form",
  theme,
  className,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames(
      className,
      "option",

      "option--" + theme,
      {
        "option-underline": hasLineAfter
      }
    )}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <RepeatOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
      <ChevronIcon className="option__chevron option__chevron--right" />
    </div>
  </div>
);

const OnConvertToStatic: React.FC<IOptionProps> = ({
  onClick,
  title = "Cancel Recurring",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <RepeatOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMoveToOption: React.FC<IOptionProps> = ({ onClick, title = "Move", theme }): JSX.Element => {
  const [isOpenSectionsList, setIsOpenSectionsList] = useState<boolean>(false);

  const moveButton: any = useRef(null);
  return (
    <>
      <div
        onClick={() => setIsOpenSectionsList(true)}
        ref={moveButton}
        className={classnames(
          "option",

          "option--" + theme,
          {
            "option--selected": isOpenSectionsList
          }
        )}
      >
        <div className={getClassName(theme)}>
          <div className="icon-wrapper">
            <DriveFileMoveOutlinedIcon className="icon" />
          </div>
          <span className="title">{title}</span>
          <ChevronIcon className="option__chevron option__chevron--right" />
        </div>
      </div>
      <Popper
        disablePortal
        open={isOpenSectionsList}
        anchorEl={moveButton.current}
        placement="right"
        sx={{ marginLeft: "30px" }}
      >
        <MoveToFolder sendData={onClick} isOpen={isOpenSectionsList} setIsOpen={setIsOpenSectionsList} />
      </Popper>
    </>
  );
};

const OnImportOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Import from Template",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <WorkspaceImportTemplateIcon className="icon" sx={{ fontSize: "20px" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnFileInfo: React.FC<IOptionProps> = ({ onClick, title = "File Info", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <NoteAddOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnAddNoteOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Add a new note",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <StickyNote2OutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnNoteInfo: React.FC<IOptionProps> = ({ onClick, title = "Note info", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <InfoOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnAddClosingDate: React.FC<IOptionProps> = ({
  onClick,
  title = "Set date",
  theme,
  className,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames(
      className,
      "option",

      "option--" + theme,
      {
        "option-underline": hasLineAfter
      }
    )}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <CalendarIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMonitoringData: React.FC<IOptionProps> = ({
  onClick,
  title = "Deal Metrics",
  theme,
  className,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames(
      className,
      "option",

      "option--" + theme,
      {
        "option-underline": hasLineAfter
      }
    )}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <DataIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnDeleteDeal: React.FC<IOptionProps> = ({
  onClick,
  title = "Delete deal",
  theme,
  className,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames(
      className,
      "option",

      "option--" + theme,
      {
        "option-underline": hasLineAfter
      }
    )}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <DeleteOutlineIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnExportDeal: React.FC<IOptionProps> = ({
  onClick,
  title = "Export deal",
  theme,
  className,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames(
      className,
      "option",

      "option--" + theme,
      {
        "option-underline": hasLineAfter
      }
    )}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <ExportIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMeasureDeleteOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Delete",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMeasureDistributeOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Distribute Values",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnMeasureUndistributeOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Undistribute Values",
  theme,
  hasLineAfter
}): JSX.Element => {
  return (
    <div
      onClick={onClick}
      className={classnames("option", "option--" + theme, {
        "option-underline": hasLineAfter
      })}
    >
      <div className={getClassName(theme)}>
        <span className="title">{title}</span>
      </div>
    </div>
  );
};

const OnExportOption: React.FC<IOptionProps> = ({ onClick, title = "Export", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <UploadSharp className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnAddValueFromPDF: React.FC<IOptionProps> = ({
  onClick,
  title = "Add value from PDF",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <Stack sx={{ flexDirection: "row", alignItems: "center", width: "100%" }}>
        <div className="icon-wrapper">
          <NoteAddIcon className="icon" />
        </div>
        <Stack
          sx={{ flexDirection: "row", alignItems: "center", gap: "4px", justifyContent: "space-between", flexGrow: 1 }}
        >
          <span className="title">{title}</span>
          <Tooltip title="Optimized for capital account statements and cashflow notices">
            <InfoOutlinedIcon />
          </Tooltip>
        </Stack>
      </Stack>
    </div>
  </div>
);

const OnAnalyzeFileWithGPT: React.FC<IOptionProps> = ({
  onClick,
  title = "Analyze file with GPT",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <FileOpenIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnAddTask: React.FC<IOptionProps> = ({ onClick, title = "Add Task", theme, hasLineAfter }): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <TaskAltOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnGoToDataRoom: React.FC<IOptionProps> = ({
  onClick,
  title = "Go to Data Room",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <CloudDoneOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const OnImportFilesToDeal: React.FC<IOptionProps> = ({
  onClick,
  title = "Index files & Sync",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <ExitToAppOutlinedIcon className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const onMultiplePdfTagOption: React.FC<IOptionProps> = ({
  onClick,
  title = "Tag PDF files",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <WorkspaceTagPdfIcon className="icon" sx={{ fontSize: "20px" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const onSetPrivate: React.FC<IOptionProps> = ({
  onClick,
  title = "Set to private",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <VisibilityLock className="icon" style={{ color: "#597380", fill: "#597380" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const ExtractFileK1: React.FC<IOptionProps> = ({
  onClick,
  title = "Download and Extract K1",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <NoteAddOutlinedIcon sx={{ fontSize: "20px" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const onCategorize: React.FC<IOptionProps> = ({
  onClick,
  title = "Categorize file",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <InsertDriveFileOutlinedIcon sx={{ fontSize: "20px" }} className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const onDataExtraction: React.FC<IOptionProps> = ({
  onClick,
  title = "Data Extraction",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <InsertDriveFileOutlinedIcon sx={{ fontSize: "20px" }} className="icon" />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

const onLookThrough: React.FC<IOptionProps> = ({
  onClick,
  title = "Look Through",
  theme,
  hasLineAfter
}): JSX.Element => (
  <div
    onClick={onClick}
    className={classnames("option", "option--" + theme, {
      "option-underline": hasLineAfter
    })}
  >
    <div className={getClassName(theme)}>
      <div className="icon-wrapper">
        <Hierarchy sx={{ fontSize: "20px", fill: "#5d7782" }} />
      </div>
      <span className="title">{title}</span>
    </div>
  </div>
);

export type ContextMenuOptionFunctionType = (arg?: any) => Promise<void> | void;

export type ContextMenuOptionObjectType = {
  title?: string;
  action: ContextMenuOptionFunctionType;
  withDatePicker?: boolean;
};

export type ContextMenuOptionValue = ContextMenuOptionFunctionType | ContextMenuOptionObjectType;

export type ContextMenuOptionType =
  | "onRename"
  | "onDelete"
  | "onDuplicate"
  | "onDownload"
  | "onUpload"
  | "onMoveTo"
  | "onShare"
  | "onEdit"
  | "onAddForm"
  | "onLink"
  | "onSaveDealAsTemplate"
  | "onCreateFolder"
  | "onMarkAsRead"
  | "onMarkAsUnread"
  | "onRemoveNotification"
  | "onConvertToPeriodic"
  | "onConvertToStatic"
  | "onImport"
  | "onFileInfo"
  | "onAddNote"
  | "onNoteInfo"
  | "onAddClosingDate"
  | "onExport"
  | "onMeasureDelete"
  | "onMeasureDistribute"
  | "onMeasureUndistribute"
  | "onMonitoringData"
  | "onDeleteDeal"
  | "onExportDeal"
  | "onAddValueFromPDF"
  | "onAnalyzeFileWithGPT"
  | "onAddTask"
  | "onGoToDataRoom"
  | "onImportFilesToDeal"
  | "onMultiplePdfTagOption"
  | "onSetPrivate"
  | "onExtractFileK1"
  | "onCategorize"
  | "onDataExtraction"
  | "onLookThrough";
export type ContextMenuOptions = Partial<Record<ContextMenuOptionType, ContextMenuOptionValue>>;

type OptionsHashType = Record<ContextMenuOptionType, CustomComponent<IOptionProps>>;

export const OptionsHash: OptionsHashType = {
  onRename: OnRenameOption,
  onDelete: OnDeleteOption,
  onDuplicate: OnDuplicateOption,
  onDownload: OnDownloadOption,
  onMoveTo: OnMoveToOption,
  onShare: OnShareOption,
  onEdit: OnEditOption,
  onAddForm: OnAddFormOption,
  onLink: OnLinkOption,
  onUpload: OnUploadOption,
  onSaveDealAsTemplate: OnSaveDealAsTemplateOption,
  onCreateFolder: OnCreateFolderOption,
  onMarkAsRead: OnMarkAsReadOption,
  onMarkAsUnread: OnMarkAsUnreadOption,
  onRemoveNotification: OnRemoveNotificationOption,
  onConvertToPeriodic: OnConvertToPeriodic,
  onConvertToStatic: OnConvertToStatic,
  onImport: OnImportOption,
  onFileInfo: OnFileInfo,
  onAddNote: OnAddNoteOption,
  onNoteInfo: OnNoteInfo,
  onAddClosingDate: OnAddClosingDate,
  onMeasureDelete: OnMeasureDeleteOption,
  onMeasureDistribute: OnMeasureDistributeOption,
  onMeasureUndistribute: OnMeasureUndistributeOption,
  onMonitoringData: OnMonitoringData,
  onDeleteDeal: OnDeleteDeal,
  onExportDeal: OnExportDeal,
  onExport: OnExportOption,
  onAddValueFromPDF: OnAddValueFromPDF,
  onAnalyzeFileWithGPT: OnAnalyzeFileWithGPT,
  onAddTask: OnAddTask,
  onGoToDataRoom: OnGoToDataRoom,
  onImportFilesToDeal: OnImportFilesToDeal,
  onMultiplePdfTagOption: onMultiplePdfTagOption,
  onSetPrivate: onSetPrivate,
  onExtractFileK1: ExtractFileK1,
  onCategorize: onCategorize,
  onDataExtraction: onDataExtraction,
  onLookThrough: onLookThrough
};
